(function () {
  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
      return;
    }

    callback();
  };

  /**
   * True when any part of the group is already in the viewport.
   * ScrollTrigger start is typically "top 80%", so a group sitting in the
   * top 80% of the screen has not crossed that line yet — and with Lenis
   * onEnter often does not fire until the first wheel event. Hiding those
   * groups leaves above-the-fold content blank (ARB-205).
   */
  function groupIsOnScreen(el) {
    const rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  }

  function initContentRevealScroll() {
    if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') {
      return;
    }

    window.gsap.registerPlugin(window.ScrollTrigger);

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const ctx = window.gsap.context(() => {
      document.querySelectorAll('[data-reveal-group]').forEach((groupEl) => {
        if (groupEl.hasAttribute('data-reveal-ready')) return;
        groupEl.setAttribute('data-reveal-ready', '');

        const groupStaggerSec = (parseFloat(groupEl.getAttribute('data-stagger')) || 100) / 1000;
        const groupDistance = groupEl.getAttribute('data-distance') || '2em';
        const triggerStart = groupEl.getAttribute('data-start') || 'top 80%';

        const animDuration = 0.8;
        const animEase = 'power4.inOut';

        if (prefersReduced) {
          window.gsap.set(groupEl, { clearProps: 'all', y: 0, autoAlpha: 1 });
          return;
        }

        if (groupIsOnScreen(groupEl)) {
          return;
        }

        const directChildren = Array.from(groupEl.children).filter((el) => el.nodeType === 1);
        if (!directChildren.length) {
          window.gsap.set(groupEl, { y: groupDistance, autoAlpha: 0 });
          window.ScrollTrigger.create({
            trigger: groupEl,
            start: triggerStart,
            once: true,
            onEnter: () =>
              window.gsap.to(groupEl, {
                y: 0,
                autoAlpha: 1,
                duration: animDuration,
                ease: animEase,
                onComplete: () => window.gsap.set(groupEl, { clearProps: 'all' }),
              }),
          });
          return;
        }

        const slots = [];
        directChildren.forEach((child) => {
          const nestedGroup = child.matches('[data-reveal-group-nested]')
            ? child
            : child.querySelector(':scope [data-reveal-group-nested]');

          if (nestedGroup) {
            const includeParent =
              child.getAttribute('data-ignore') !== 'true' &&
              (child.getAttribute('data-ignore') === 'false' ||
                nestedGroup.getAttribute('data-ignore') === 'false');

            const nestedChildren = Array.from(nestedGroup.children).filter(
              (el) => el.nodeType === 1 && el.getAttribute('data-ignore') !== 'true',
            );

            slots.push({
              type: 'nested',
              parentEl: child,
              nestedEl: nestedGroup,
              includeParent,
              nestedChildren,
            });
          } else {
            if (child.getAttribute('data-ignore') === 'true') return;
            slots.push({ type: 'item', el: child });
          }
        });

        slots.forEach((slot) => {
          if (slot.type === 'item') {
            const isNestedSelf = slot.el.matches('[data-reveal-group-nested]');
            const d = isNestedSelf ? groupDistance : slot.el.getAttribute('data-distance') || groupDistance;
            window.gsap.set(slot.el, { y: d, autoAlpha: 0 });
          } else {
            if (slot.includeParent) window.gsap.set(slot.parentEl, { y: groupDistance, autoAlpha: 0 });
            const nestedD = slot.nestedEl.getAttribute('data-distance') || groupDistance;
            slot.nestedChildren.forEach((target) => window.gsap.set(target, { y: nestedD, autoAlpha: 0 }));
          }
        });

        slots.forEach((slot) => {
          if (slot.type === 'nested' && slot.includeParent) {
            window.gsap.set(slot.parentEl, { y: groupDistance });
          }
        });

        window.ScrollTrigger.create({
          trigger: groupEl,
          start: triggerStart,
          once: true,
          onEnter: () => {
            const tl = window.gsap.timeline();
            slots.forEach((slot, slotIndex) => {
              const slotTime = slotIndex * groupStaggerSec;
              if (slot.type === 'item') {
                tl.to(
                  slot.el,
                  {
                    y: 0,
                    autoAlpha: 1,
                    duration: animDuration,
                    ease: animEase,
                    onComplete: () => window.gsap.set(slot.el, { clearProps: 'all' }),
                  },
                  slotTime,
                );
              } else {
                if (slot.includeParent) {
                  tl.to(
                    slot.parentEl,
                    {
                      y: 0,
                      autoAlpha: 1,
                      duration: animDuration,
                      ease: animEase,
                      onComplete: () => window.gsap.set(slot.parentEl, { clearProps: 'all' }),
                    },
                    slotTime,
                  );
                }
                const nestedMs = parseFloat(slot.nestedEl.getAttribute('data-stagger'));
                const nestedStaggerSec = isNaN(nestedMs) ? groupStaggerSec : nestedMs / 1000;
                slot.nestedChildren.forEach((nestedChild, nestedIndex) => {
                  tl.to(
                    nestedChild,
                    {
                      y: 0,
                      autoAlpha: 1,
                      duration: animDuration,
                      ease: animEase,
                      onComplete: () => window.gsap.set(nestedChild, { clearProps: 'all' }),
                    },
                    slotTime + nestedIndex * nestedStaggerSec,
                  );
                });
              }
            });
          },
        });
      });
    });

    return () => ctx.revert();
  }

  window.initContentRevealScroll = initContentRevealScroll;

  ready(() => {
    requestAnimationFrame(() => {
      initContentRevealScroll();

      if (typeof window.ScrollTrigger !== 'undefined') {
        window.ScrollTrigger.refresh();
      }
    });
  });

  window.addEventListener('load', () => {
    if (typeof window.ScrollTrigger !== 'undefined') {
      window.ScrollTrigger.refresh();
    }
  });

  document.addEventListener('shopify:section:load', () => {
    initContentRevealScroll();
  });
})();
